import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../services/api";
import type { Tenant } from "../../../types/tenant";
import { BackButton } from "../../../components/button/back-button";
import { useToast } from "../../../contexts/toast/toast-context";
import {
  formatPhoneWithCountryCode,
  getApiErrorMessage,
  normalizePhone,
} from "../../../utils";
import { LoadingButton } from "../../../components/button/loading-button";

const APP_NAME_ONLY_LETTERS_REGEX = /^[A-Za-z]+$/;

import { useTenantFeatures } from "../../../contexts/tenant-features";

export default function CadastroTenant() {
  const { refresh } = useTenantFeatures();
  const { error, success } = useToast();

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState<Partial<Tenant>>({
    appName: "",
    phone: "",
    email: "",
    prompt: "",
    gptModel: "gpt-5.4-mini",
    enabledScheduling: false,
    enabledAi: false,
    enabledRag: false,
    enabledAttendantMessageBuffer: false,
    marketingWhatsappConsentRequired: false,
    schedulingTime: 60,
    outsideHoursMessageEnabled: false,
    outsideHoursMessage: "",
    schedulingStartTime: "07:00",
    schedulingEndTime: "18:00",
    schedulingSaturdayStartTime: "07:00",
    schedulingSaturdayEndTime: "18:00",
    schedulingAllowSaturday: false,
    schedulingWeeklyAvailability: {},
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      try {
        api.get<Tenant>(`/tenant/${id}`).then((res) => {
          setTenant({
            ...res.data,
            phone: formatPhoneWithCountryCode(res.data.phone),
            outsideHoursMessageEnabled: !!res.data.outsideHoursMessageEnabled,
            outsideHoursMessage: res.data.outsideHoursMessage ?? "",
            schedulingSaturdayStartTime:
              res.data.schedulingSaturdayStartTime ??
              res.data.schedulingStartTime ??
              "07:00",
            schedulingSaturdayEndTime:
              res.data.schedulingSaturdayEndTime ??
              res.data.schedulingEndTime ??
              "18:00",
          });
        });
      } catch (err) {
        const apiMessage = getApiErrorMessage(err);
        error(apiMessage);
      }
    }
  }, [id, error]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    if (name === "appName") {
      setTenant({
        ...tenant,
        appName: value.replace(/[^A-Za-z]/g, ""),
      });
      return;
    }

    if (name === "phone") {
      setTenant({
        ...tenant,
        phone: formatPhoneWithCountryCode(value),
      });
      return;
    }

    setTenant({ ...tenant, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const appName = (tenant.appName || "").trim();
    if (!APP_NAME_ONLY_LETTERS_REGEX.test(appName)) {
      error("App Name deve conter apenas letras (A-Z, a-z).");
      return;
    }

    try {
      setIsSubmitting(true);
      const {
        customerSheetSettings: _customerSheetSettings,
        establishmentLatitude: _establishmentLatitude,
        establishmentLongitude: _establishmentLongitude,
        establishmentLocationName: _establishmentLocationName,
        establishmentAddress: _establishmentAddress,
        ...tenantData
      } = tenant;
      const payload: Partial<Tenant> = {
        ...tenantData,
        appName,
        phone: normalizePhone(tenant.phone || ""),
      };
      if (id) {
        await api.patch(`/tenant/${id}`, payload);
        success("Tenant atualizado com sucesso!");
      } else {
        await api.post("/tenant", payload);
        success("Tenant cadastrado com sucesso!");
      }
      refresh();
      navigate("/painel/config/tenant");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app-page">
      <BackButton />
      <h1 className="app-page-title mb-6">
        {id ? "Editar Tenant" : "Novo Tenant"}
      </h1>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="form-label">App Name</label>
          <input
            type="text"
            name="appName"
            value={tenant.appName || ""}
            onChange={handleChange}
            className="form-input"
            pattern="[A-Za-z]+"
            title="Use apenas letras (A-Z, a-z)."
            required
          />
        </div>
        <div>
          <label className="form-label">WhatsApp</label>
          <input
            type="text"
            name="phone"
            value={tenant.phone || ""}
            onChange={handleChange}
            className="form-input"
            required
          />
        </div>
        <div>
          <label className="form-label">Email</label>
          <input
            type="email"
            name="email"
            value={tenant.email || ""}
            onChange={handleChange}
            className="form-input"
            required
          />
        </div>
        <section className="app-card space-y-4 p-4">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              name="marketingWhatsappConsentRequired"
              checked={!!tenant.marketingWhatsappConsentRequired}
              onChange={(e) =>
                setTenant({
                  ...tenant,
                  marketingWhatsappConsentRequired: e.target.checked,
                })
              }
              className="form-checkbox mt-0.5"
            />
            <span>
              <span className="block text-sm text-slate-700">
                Exigir resposta ao consentimento de marketing no WhatsApp
              </span>
              <span className="mt-1 block text-xs text-slate-500">
                Antes de iniciar um novo atendimento, solicita ao contato que
                aceite ou recuse comunicações de ofertas, novidades e promoções.
              </span>
            </span>
          </label>
        </section>

        <section className="app-card space-y-4 p-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="enabledAi"
              checked={tenant.enabledAi}
              onChange={(e) => {
                const enabledAi = e.target.checked;
                setTenant({
                  ...tenant,
                  enabledAi,
                  enabledRag: enabledAi ? !!tenant.enabledRag : false,
                });
              }}
              className="form-checkbox"
            />
            <label className="text-sm text-slate-700">
              Inteligência Artificial
            </label>
          </div>

          {tenant.enabledAi && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="enabledRag"
                checked={!!tenant.enabledRag}
                onChange={(e) =>
                  setTenant({ ...tenant, enabledRag: e.target.checked })
                }
                className="form-checkbox"
              />
              <label className="text-sm text-slate-700">RAG</label>
            </div>
          )}
        </section>

        <section className="app-card space-y-4 p-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="enabledAttendantMessageBuffer"
              checked={!!tenant.enabledAttendantMessageBuffer}
              onChange={(e) =>
                setTenant({
                  ...tenant,
                  enabledAttendantMessageBuffer: e.target.checked,
                })
              }
              className="form-checkbox"
            />
            <label className="text-sm text-slate-700">
              Agrupar mensagens do atendente por 15 segundos
            </label>
          </div>
        </section>

        <section className="app-card space-y-4 p-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="enabledScheduling"
              checked={tenant.enabledScheduling}
              onChange={(e) =>
                setTenant({ ...tenant, enabledScheduling: e.target.checked })
              }
              className="form-checkbox"
            />
            <label className="text-sm text-slate-700">
              Agendamento de consultas
            </label>
          </div>

          <p className="text-xs text-slate-500">
            Confirmações e lembretes são configurados na página de Configurações
            de agendamento.
          </p>
        </section>

        <section className="app-card p-4 space-y-4">
          <h2 className="app-section-title">Protocolos</h2>
          <label className="flex gap-2 items-center">
            <input
              type="checkbox"
              checked={!!tenant.protocolsEnabled}
              onChange={(e) =>
                setTenant({ ...tenant, protocolsEnabled: e.target.checked })
              }
            />
            Habilitar protocolos
          </label>
          <p className="text-sm text-slate-500">
            Prazo em minutos úteis, usando os horários de atendimento já
            configurados. Deixe vazio para não definir SLA. Prazos existentes
            permanecem fixos.
          </p>
          <div className="grid gap-3 md:grid-cols-4">
            {(["LOW", "NORMAL", "HIGH", "CRITICAL"] as const).map(
              (priority, i) => (
                <label key={priority} className="form-label">
                  {["Baixa", "Normal", "Alta", "Crítica"][i]}
                  <input
                    className="form-input"
                    type="number"
                    min={1}
                    max={525600}
                    step={1}
                    value={tenant.protocolSlaSettings?.[priority] ?? ""}
                    onChange={(e) => {
                      const settings = { ...tenant.protocolSlaSettings };
                      if (e.target.value)
                        settings[priority] = Number(e.target.value);
                      else delete settings[priority];
                      setTenant({ ...tenant, protocolSlaSettings: settings });
                    }}
                  />
                </label>
              ),
            )}
          </div>
        </section>
        <LoadingButton
          type="submit"
          className="btn btn-neutral cursor-pointer"
          isLoading={isSubmitting}
          loadingLabel={id ? "Salvando" : "Cadastrando"}
        >
          {id ? "Salvar Alterações" : "Cadastrar Tenant"}
        </LoadingButton>
      </form>
    </div>
  );
}
